<template>
  <page-list
    :list="list"
    :columns="columns"
    :single-actions="singleActions"
    :group-actions="groupActions"
    :export-data-options="exportDataOptions">
    <template v-slot:group-actions-append>
      <div class="select-profix d-flex ml-2">
        <div class="label">{{$t('bill.effective_month')}}</div>
        <a-range-picker :mode="['month', 'month']" format="YYYY-MM" v-model="selectedMonth" @panelChange="panelChange" />
      </div>
    </template>
  </page-list>
</template>

<script>
import moment from 'moment'
import XLSX from 'xlsx'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { getTimeTableColumn } from '@/utils/common/tableColumn'
import storage from '@/utils/storage'
import {
  getContractNumberFilter,
  getServiceDescriptionFilter,
  getRegionFilter,
  getOsTypeFilter,
  getModelFilter,
} from '../../utils/filters'
import {
  getContractNumberColumn,
  getServicePartNumberColumn,
  getServiceDescriptionColumn,
  getRegionColumn,
  getModelColumn,
  getPromiseMonthColumn,
  getCppCountColumn,
  getUnitPriceColumn,
  getAmountPriceColumn,
  getVatColumn,
  getAmountColumn,
  getEffectiveMonthColumn,
  getExpirationMonthColumn,
  getOsTypeTableColumn,
} from '../../utils/columns'

const localTimeKey = '__oc_date_time_'
const timeMap = {
  month: {
    start: moment().startOf('month'),
    end: moment().endOf('month'),
  },
  last_month: {
    start: moment().subtract(1, 'month').startOf('month'),
    end: moment().subtract(1, 'month').endOf('month'),
  },
  quarter: {
    start: moment().startOf('quarter'),
    end: moment().endOf('quarter'),
  },
  last_quarter: {
    start: moment().subtract(1, 'quarter').startOf('quarter'),
    end: moment().subtract(1, 'quarter').endOf('quarter'),
  },
  year: {
    start: moment().startOf('year'),
    end: moment().endOf('year'),
  },
}

export default {
  name: 'BillAccountCppList',
  mixins: [WindowsMixin, ListMixin],
  props: {
    id: {
      type: String,
      default: 'BillCppForBillAccountSidePage',
    },
    data: Object,
  },
  data () {
    return {
      selectedMonth: [],
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'reservations',
        apiVersion: 'v1',
        getParams: this.getParam,
        filterOptions: {
          offering_class: getContractNumberFilter(),
          name: getServiceDescriptionFilter(),
          cpp_region: getRegionFilter(),
          os_type: getOsTypeFilter(),
          os_sku: getModelFilter(),
          cpp_shared_status: {
            label: this.$t('common.status'),
            dropdown: true,
            items: [
              { label: this.$t('status.cpp.assigned'), key: '100' },
              { label: this.$t('status.cpp.unassigned'), key: '0' },
            ],
          },
        },
        hiddenColumns: ['offering_class', 'payment_option', 'created_at'],
        // steadyStatus: Object.values(expectStatus.cpp).flat(),
      }),
      groupActions: [
        {
          label: this.$t('bill.import_data'),
          permission: '',
          action: () => {
            this.createDialog('CppImprotDialog', {
              account_id: this.data.id,
              onManager: this.onManager,
              refresh: this.refresh,
            })
          },
          meta: () => {
            return {
              buttonType: 'primary',
            }
          },
        },
        {
          label: this.$t('bill.download_template'),
          permission: '',
          action: () => {
            this.downloadTemplate()
          },
        },
      ],
      exportDataOptions: {
        items: [
          { label: this.$t('bill.contract_number'), key: 'offering_class' },
          { label: this.$t('bill.service_part_number'), key: 'payment_option' },
          { label: this.$t('bill.service_description'), key: 'category' },
          { label: this.$t('bill.text_89'), key: 'cpp_shared_status' },
          { label: this.$t('bill.text_83'), key: 'cpp_region' },
          { label: this.$t('table.title.os'), key: 'os_type' },
          { label: this.$t('bill.model'), key: 'os_sku' },
          { label: this.$t('bill.promise_month'), key: 'reservation_months' },
          { label: this.$t('bill.count'), key: 'cpp_count' },
          { label: this.$t('bill.unit_price'), key: 'cpp_rate' },
          { label: this.$t('bill.vat'), key: 'vat' },
          { label: this.$t('bill.amount_price'), key: 'monthly_cost' },
          { label: this.$t('bill.amount'), key: 'cpp_total_cost' },
          { label: this.$t('bill.effective_month'), key: 'usage_start_time' },
          { label: this.$t('bill.expiration_month'), key: 'usage_end_time' },
          { label: this.$t('bill.import_time'), key: 'created_at' },
        ],
        transformParams: (params) => {
          return { ...params, cloudaccount_id: this.data.id }
        },
      },
      singleActions: [
        {
          label: this.$t('bill.shared'),
          permission: '',
          action: (obj) => {
            let title = this.$t('bill.shared')
            if (obj.cpp_shared_status === 100) {
              title = this.$t('common_224')
            }
            this.createDialog('CppSharedDialog', {
              vm: this,
              domain_id: this.data.domain_id,
              resId: obj.id,
              data: [obj],
              columns: this.columns,
              title,
              name: this.$t('bill.cpp_shared'),
              onManager: this.onManager,
              refresh: this.refresh,
            })
          },
          // meta: (obj) => {
          //   return {
          //     validate: obj.cpp_shared_status !== 100,
          //   }
          // },
        },
        {
          label: this.$t('bill.clear_shared'),
          permission: '',
          action: (obj) => {
            this.createDialog('CppSharedClearDialog', {
              vm: this,
              domain_id: this.data.domain_id,
              resId: obj.id,
              data: [obj],
              columns: this.columns,
              title: this.$t('bill.clear_shared'),
              name: this.$t('bill.cpp_shared'),
              onManager: this.onManager,
              refresh: this.refresh,
            })
          },
          meta: (obj) => {
            return {
              validate: obj.cpp_shared_status !== 0,
            }
          },
        },
        {
          label: this.$t('bill.view_record'),
          permission: '',
          action: (obj) => {
            this.createDialog('CppActionRecordsDialog', {
              data: [obj],
            })
          },
        },
        {
          label: this.$t('table.action.delete'),
          action: obj => {
            this.createDialog('DeleteResDialog', {
              vm: this,
              data: [obj],
              columns: this.columns,
              title: this.$t('table.action.delete'),
              name: this.$t('bill.cpp'),
              onManager: this.onManager,
            })
          },
          meta: obj => this.$getDeleteResult(obj),
        },
      ],
    }
  },
  computed: {
    columns () {
      return [
        getServiceDescriptionColumn({ edit: true, onManager: this.onManager }),
        getContractNumberColumn(),
        getServicePartNumberColumn(),
        {
          field: 'cpp_shared_status',
          title: this.$t('common.status'),
          minWidth: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class='text-truncate'>
                  <status status={ row.cpp_shared_status === 100 ? 'assigned' : 'unassigned' } statusModule='cpp' />
                </div>,
              ]
            },
          },
        },
        getRegionColumn(),
        getOsTypeTableColumn(),
        getModelColumn(),
        getPromiseMonthColumn(),
        getCppCountColumn(),
        getUnitPriceColumn(),
        getAmountPriceColumn(),
        getVatColumn(),
        getAmountColumn(),
        getEffectiveMonthColumn(),
        getExpirationMonthColumn(),
        getTimeTableColumn({ title: this.$t('bill.import_time') }),
      ]
    },
  },
  watch: {
    selectedMonth (val) {
      this.fetchData()
    },
  },
  created () {
    this.initTime()
    this.fetchData()
  },
  methods: {
    initTime () {
      const localTime = storage.get(localTimeKey, {})
      const { dateMode, time = { } } = localTime
      let selectedMonth = []
      if (timeMap[dateMode]) {
        selectedMonth = [timeMap[dateMode].start, timeMap[dateMode].end]
      } else {
        selectedMonth = [this.$moment(time.start).startOf('month'), this.$moment(time.end).endOf('month')]
      }
      if (!selectedMonth.length) {
        return
      }
      if (!this.selectedMonth.length) {
        this.selectedMonth = selectedMonth
        return
      }
      if (this.$moment(selectedMonth[0]).format('YYYYMM') === this.$moment(this.selectedMonth[0]).format('YYYYMM') &&
        this.$moment(selectedMonth[1]).format('YYYYMM') === this.$moment(this.selectedMonth[1]).format('YYYYMM')) {
        return
      }
      this.selectedMonth = selectedMonth
    },
    panelChange (value) {
      this.selectedMonth = value
      this.setLocalTime(value)
    },
    setLocalTime (value) {
      if (value[0] && value[1]) {
        const start = this.$moment(value[0]).format('YYYYMM')
        const end = this.$moment(value[1]).format('YYYYMM')
        let dateMode = 'custom'
        let time = {}
        for (const mode in timeMap) {
          if (start === timeMap[mode].start.format('YYYYMM') && end === timeMap[mode].end.format('YYYYMM')) {
            dateMode = mode
          }
        }
        if (dateMode === 'custom') {
          time = { start: this.$moment(value[0]).startOf('month'), end: this.$moment(value[1]).endOf('month') }
        }
        storage.set(localTimeKey, {
          dateMode,
          time: dateMode === 'custom' ? time : { },
        })
      }
    },
    getParam () {
      const ret = {
        cloudaccount_id: this.data.id,
        reservation_type: 'azure_cpp',
      }
      if (this.selectedMonth[0] && this.selectedMonth[1]) {
        ret.start_day = this.$moment(this.selectedMonth[0]).startOf('month').format('YYYYMMDD')
        ret.end_day = this.$moment(this.selectedMonth[1]).endOf('month').format('YYYYMMDD')
      }
      return ret
    },
    refresh () {
      this.list.refresh()
    },
    fetchData () {
      this.list.fetchData()
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'BudgetListSidePage', {
        id: row.id,
        resource: () => {
          return {
            data: row,
          }
        },
      }, {
        list: this.list,
      })
    },
    downloadTemplate () {
      const data = [[
        this.$t('bill.contract_number'),
        this.$t('bill.service_description'),
        this.$t('bill.text_83'),
        this.$t('bill.service_part_number'),
        this.$t('table.title.os'),
        this.$t('bill.model'),
        this.$t('bill.effective_time'),
        this.$t('bill.expiration_time'),
        this.$t('bill.count'),
        this.$t('bill.unit_price'),
        this.$t('bill.amount_price'),
        this.$t('bill.vat'),
        this.$t('bill.amount'),
      ],
      [
        '7-YSQ',
        'Virtual Machines Dv2/DSv2 Series Windows - D4 v2/DS4 v2 - CN North 2',
        'CN North 2',
        'AAD-22217',
        'Windows',
        'D4 v2/DS4 v2',
        '2021/12/1',
        '2022/1/1',
        3,
        '200.00',
        '600.00',
        '36.00',
        '636.00',
`样例，导入前请删除
合同号：按照实际填写，可以为空
服务说明：需保证和账单中产品列一致
服务部件号：按照实际填写，可以为空
单价：不含税单价
总价：等于单价*数量*承诺月份
VAT：指税值，按照实际合同填写
总金额：等于总价+VAT，含税总金`,
      ]]
      const filename = `${this.$t('bill.cpp_template')}.xlsx`
      const ws_name = 'Sheet1'
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(data)
      ws['!cols'] = [
        { wch: 7.5 },
        { wch: 70 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 26 },
        { wch: 26 },
        { wch: 5 },
        { wch: 7.5 },
        { wch: 7.5 },
        { wch: 7.5 },
        { wch: 7.5 },
        { wch: 50 },
      ]
      ws['!rows'] = [
        { hpt: 20 },
        { hpt: 150 },
      ]
      XLSX.utils.book_append_sheet(wb, ws, ws_name)
      XLSX.writeFile(wb, filename)
    },
  },
}
</script>

<style lang="less" scoped>
.select-profix {
  .label {
    border-left: 1px solid #d9d9d9;
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
    line-height: 30px;
    background: #f2f1f1;
    padding: 0 10px;
    text-align: center;
    min-width: fit-content;
    height: 32px;
  }
}
</style>
